import React, { useContext, useMemo } from 'react';

import { downloadUrl, DownloadURLOptions, openBlob } from '@apis/common';
import { dashboardCustomsRequestApis } from '@apis/next/customsRequestApis';
import { isAxiosErrorOfStatus } from '@apis/next/errors';
import { fileUploadApis } from '@apis/next/fileUploadApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import fileIcon from '@assets/img/icons/file.svg';
import selectFiles from '@chenyuwang/select-files';
import { Button } from '@components/Button';
import { FileLink } from '@components/FileLink';
import { QueryContainer } from '@components/QueryContainer';
import { CustomsInstructionContext } from '@components/utilities/Bookings/helpers2';
import { arrivalNoticeStrings } from '@data/arrivalNotice';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { customsRequestStrings } from '@data/customsRequest';
import { instructionStrings } from '@data/instruction';
import { preAlertStrings } from '@data/preAlert';
import { usePostBookingInquirySalespersonCustoms } from '@hooks/mutation/dashboard/usePostBookingInquirySalespersonCustoms';
import { useDownloadAndCompress } from '@hooks/mutation/useDownloadAndCompress';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import { RemoteFile } from '@models/Files';
import { CustomsRequestHelper } from '@models/next/CustomsRequest';
import { CustomsStatusName, CustomsRequestStatusName } from '@models/next/CustomsRequestStatus';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { id, mapOptional, mapQueriesData, toJST } from '@utils/types';
import clsx from 'clsx';
import { isLeft } from 'fp-ts/lib/Either';
import { pipe } from 'remeda';

const strings = createStrings({
  ja: {
    downloadAll: '一括ダウンロード',
    noFiles: 'ファイルがありません',
    createCustomsRequest: `${customsRequestStrings.customsRequest}を作成する`,
    cannotCreateCustomsRequestReason: '通関書類が確認済になってから作成できます。',
    becomeSalesperson: '担当者になる',
    becomeSalespersonWarning: '担当者になると進めてよろしいでしょうか？',
    customsRequestNotSubmitted: `${customsRequestStrings.customsRequest}はまだ送信されていません。`,
    acceptCustomsRequest: '通関案件の受け取り',
    customsRequestAccepted: '受け取りました',
    customRequestNotYetAccepted: 'まだ受け取りしていません。',
    accept: '受け取る',
    warningComplete: 'この案件を受け取りますか？',
    customsRequestFooterStatusMap: id<
      Partial<Record<CustomsRequestStatusName | CustomsStatusName, string>>
    >({
      通関依頼書作成済: '作成済',
      通関依頼書送信済: '送信済',
    }),
    upload: 'アップロード',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  customsInstructionFilesContainer: {},
  customsInstructionFilesTitle: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.itemsCenter,
    marginBottom: '0.5rem',
  },
  customsInstructionFileLinkSectionTitle: {
    ...common.smallerText,
    ...common.textGray500,
    marginBottom: '0.25rem',
  },
  customsInstructionFileLinkSectionContainer: {
    borderBottom: `1px dashed ${theme.palette.grey[300]}`,
    paddingBottom: '0.5rem',
  },
  customsInstructionFileLinkSectionFiles: {
    ...common.flexColumn,
    rowGap: '0.25rem',
  },
  customsInstructionFileLinkSectionListContainer: {
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  fileLinkWithEdit: {
    ...common.middleText,
    wordBreak: 'break-all',
    padding: '0.25rem 0.5rem',
    display: 'block',
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
    '& > img': {
      ...common.textPrimary,
      marginRight: 6,
      position: 'relative',
      top: 2,
    },
  },
}));

type FileLinkItem =
  | {
      // The link opens a page to edit it, but does not open the file itself
      type: 'openUrl';
      url: string;
      downloadURLOptions: DownloadURLOptions;
    }
  | { type: 'remoteFile'; link: RemoteFile }
  | {
      type: 'downloadUrl';
      downloadURLOptions: DownloadURLOptions;
    };

const mapRemoteFileToFileLink = (remoteFile: RemoteFile): FileLinkItem => ({
  type: 'remoteFile',
  link: remoteFile,
});

const FileLinkSection = ({
  title,
  files,
  placeholder,
  noBottomBorder = false,
  footer,
}: {
  title: string;
  files: FileLinkItem[];
  placeholder?: React.ReactNode;
  noBottomBorder?: boolean;
  footer?: React.ReactNode;
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div
      className={classes.customsInstructionFileLinkSectionContainer}
      style={{ borderBottom: noBottomBorder ? 0 : undefined }}
    >
      <div className={classes.customsInstructionFileLinkSectionTitle}>{title}</div>

      <div className={classes.customsInstructionFileLinkSectionFiles}>
        {files.map((file) =>
          file.type === 'downloadUrl' ? (
            <FileLink
              key={file.downloadURLOptions.url}
              variant="gray-block"
              onClick={() => downloadUrl(file.downloadURLOptions)}
            >
              {file.downloadURLOptions.name}
            </FileLink>
          ) : file.type === 'openUrl' ? (
            <FileLink
              key={file.downloadURLOptions.url}
              variant="gray-block"
              download={false}
              href={file.url}
            >
              {file.downloadURLOptions.name.split('.')[0]}
            </FileLink>
          ) : (
            <FileLink key={file.link.url} variant="gray-block" href={file.link.url} download>
              {file.link.name}
            </FileLink>
          ),
        )}

        {files.length === 0 &&
          (placeholder ?? <span className={classes.exSmallerText}>{strings.noFiles}</span>)}

        {footer}
      </div>
    </div>
  );
};

const SeaBLFile: React.FC = () => {
  const { d2dNo } = useContext(CustomsInstructionContext);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const patchPreAlert = dashboardPreAlertApis.usePatch('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: d2dNo,
    },
  });

  const preAlertQuery = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: d2dNo,
    },
  });

  const fileUpload = fileUploadApis.usePost('/file/upload', {});

  const { renderSpinnerModal, renderAlertDialog } = useFormUtils({
    mutations: [fileUpload, patchPreAlert],
  });

  const onUpload = async () => {
    const uploadFiles = await selectFiles({
      accept:
        '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,text/plain,.pdf,image/*',
      multiple: false,
    });

    if (uploadFiles && uploadFiles.length > 0) {
      const [uploadFile] = [...uploadFiles];

      const [sea_bl_file] = await fileUpload.mutateAsync({ file: [uploadFile] });

      await patchPreAlert.mutateAsync({
        sea_bl_file,
      });

      preAlertQuery.invalidate();
    }
  };

  return (
    <div className={classes.customsInstructionFileLinkSectionContainer}>
      {renderSpinnerModal()}
      {renderAlertDialog()}

      <div className={classes.customsInstructionFileLinkSectionTitle}>
        {preAlertStrings.sea_bl_file}
      </div>

      <QueryContainer queries={[preAlertQuery]}>
        {mapQueriesData({ preAlert: preAlertQuery }, ({ preAlert }) => (
          <>
            {!preAlert.sea_bl_file && (
              <Button
                color="primary"
                size="small"
                shape="round"
                type="button"
                text={strings.upload}
                onClick={onUpload}
              />
            )}

            {preAlert.sea_bl_file && (
              <span className={classes.fileLinkWithEdit}>
                <img src={fileIcon} />

                <a download href={preAlert.sea_bl_file.url} className={classes.link}>
                  {preAlert.sea_bl_file.name}
                </a>
                <a className={clsx(classes.floatRight, classes.link)} onClick={onUpload}>
                  {formStrings.change}
                </a>
              </span>
            )}
          </>
        ))}
      </QueryContainer>
    </div>
  );
};

export const CustomsInstructionFiles: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { confirm, renderDialog } = useConfirmDialog();

  const { d2dNo, customsInstruction, onRefresh } = useContext(CustomsInstructionContext);

  const customsRequestQuery = dashboardCustomsRequestApis.useGet(
    '/dashboard/customs-request/:d2d_no',
    {
      params: {
        d2d_no: d2dNo,
      },
    },
  );

  const user = useUser();
  const bookingInquiryQuery = useBookingInquiryDetailByD2dNo(d2dNo);
  const postBookingInquirySalespersonCustoms = usePostBookingInquirySalespersonCustoms(d2dNo);

  const postBookingInquirySalespersonCustomsUtils = useFormUtils({
    mutations: [postBookingInquirySalespersonCustoms],
  });

  const downloadAndCompress = useDownloadAndCompress();

  const downloadAndCompressUtils = useFormUtils({
    mutations: [downloadAndCompress],
  });

  const putCustomsRequest = dashboardCustomsRequestApis.usePut(
    '/dashboard/customs-request/:d2d_no',
    {
      queries: { action: 'COMPLETE' },
      params: { d2d_no: d2dNo },
    },
    {
      onSuccess() {
        onRefresh?.();
        customsRequestQuery?.invalidate();
      },
    },
  );

  const putCustomsRequestUtils = useFormUtils({
    mutations: [putCustomsRequest],
  });

  const preAlertQuery = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: d2dNo,
    },
  });

  const customsRequestExists = !!customsRequestQuery.data;

  const files = useMemo(() => {
    const files = {
      seaBlFile: [
        ...(preAlertQuery.data?.sea_bl_file
          ? [mapRemoteFileToFileLink(preAlertQuery.data.sea_bl_file)]
          : []),
      ],
      arrivalNotice: preAlertQuery.data?.arrival_notice_id
        ? [
            {
              type: 'openUrl',
              url: `${url(
                '/utilities/Bookings/ExportArrivalNotice',
              )}?d2dNo=${d2dNo}&isClient=${false}`,
              downloadURLOptions: {
                name: `${arrivalNoticeStrings.arrivalNotice}.pdf`,
                url: `/dashboard/pdf/ArrivalNotice/${d2dNo}/`,
                type: 'application/pdf',
              },
            },
          ]
        : [],
      customFiles: [
        ...(customsInstruction.custom_files
          ? customsInstruction.custom_files.map(mapRemoteFileToFileLink)
          : []),
        ...(customsInstruction.rcep_file
          ? [mapRemoteFileToFileLink(customsInstruction.rcep_file)]
          : []),
        ...(customsInstruction.insurance?.tag === 'INDIVIDUAL'
          ? customsInstruction.insurance.individual_insurance_files.map(mapRemoteFileToFileLink)
          : []),
      ],
      blanketInsuranceFiles:
        customsInstruction.insurance?.tag === 'BLANKET'
          ? customsInstruction.insurance.blanket_insurance_files.map(mapRemoteFileToFileLink)
          : [],
      commodities: [
        ...(customsInstruction.commodities.length > 0
          ? ([
              {
                type: 'openUrl',
                url: `${url('/admin/booking/CustomsCommodityExport')}?d2dNo=${d2dNo}`,
                downloadURLOptions: {
                  name: `${instructionStrings.commodities}.pdf`,
                  url: `/dashboard/pdf/CustomsCommodity/${d2dNo}/`,
                  type: 'application/pdf',
                },
              },
            ] as const)
          : []),
      ],
      customsRequest: customsRequestExists
        ? [
            {
              downloadURLOptions: {
                url: `/dashboard/pdf/CustomsRequest/${d2dNo}/`,
                name: `${customsRequestStrings.customsRequest}.pdf`,
                type: 'application/pdf',
              },
              url: `${url('/admin/booking/CustomsRequestDetails')}?d2d_no=${d2dNo}`,
              type: 'openUrl',
            },
          ]
        : [],
    } satisfies Record<string, FileLinkItem[]>;
    return files;
  }, [customsInstruction, customsRequestExists, preAlertQuery.data]);

  const onClickDownloadAll = async () => {
    const downloadFiles: DownloadURLOptions[] = Object.values(files)
      .flat()
      .map((f) => {
        if (f.type === 'openUrl' || f.type === 'downloadUrl') {
          return {
            ...f.downloadURLOptions,
            open: false,
          };
        } else {
          return {
            ...f.link,
            type: 'application/pdf',
            open: false,
            method: 'GET',
            credentials: 'omit',
          };
        }
      });

    const zipBlob = await downloadAndCompress.mutateAsync(downloadFiles);

    openBlob(zipBlob, `${instructionStrings.customsFiles}.zip`, 'application/zip');
  };

  const renderSalespersonCustoms = () =>
    mapQueriesData(
      {
        customsRequest: customsRequestQuery,
        user,
        bookingInquiry: bookingInquiryQuery,
        preAlert: preAlertQuery,
      },
      ({ customsRequest, user, bookingInquiry, preAlert }) => {
        const helper = new CustomsRequestHelper(customsRequest);

        const isCurrentUserSalespersonCustoms = bookingInquiry.salesperson_customs?.id === user.id;

        return (
          <div className={classes.customsInstructionFileLinkSectionContainer}>
            {postBookingInquirySalespersonCustomsUtils.renderAlertsAndSpinnerModal()}

            <div className={classes.customsInstructionFileLinkSectionTitle}>
              {bookingInquiryStrings.salespersonCustoms}
            </div>
            <div className={classes.textBlack}>
              <>
                {helper.r.submission_target && <div>{helper.r.submission_target?.company}</div>}

                {helper.isSelfSubmissionTarget() && (
                  <>
                    <span>
                      {mapOptional(
                        bookingInquiry.salesperson_customs,
                        (u) => new UserHelper(u).fullname,
                      ) ?? '-'}
                    </span>{' '}
                    {!isCurrentUserSalespersonCustoms && (
                      <a
                        className={clsx(classes.link)}
                        onClick={async () => {
                          const canBecomeResult =
                            AdminInquiryDetailHelper.canUserBecameSalespersonCustoms(
                              user,
                              preAlert,
                            );

                          if (isLeft(canBecomeResult)) {
                            await confirm({
                              message: canBecomeResult.left.messages,
                            });
                            return;
                          } else if (
                            (await confirm({
                              message: strings.becomeSalespersonWarning,
                            })) === 'confirmed'
                          ) {
                            await postBookingInquirySalespersonCustoms.mutateAsync();
                          }
                        }}
                      >
                        {strings.becomeSalesperson}
                      </a>
                    )}
                  </>
                )}
              </>

              {!customsRequest.submission_target && (
                <span className={clsx(classes.textGray500, classes.smallerText)}>
                  {strings.customsRequestNotSubmitted}
                </span>
              )}
            </div>
            <div style={{ height: 16 }} />
          </div>
        );
      },
    );

  const renderCompleteRequest = () =>
    mapQueriesData(
      {
        customsRequest: customsRequestQuery,
        user,
        bookingInquiry: bookingInquiryQuery,
      },
      ({ customsRequest, user, bookingInquiry }) => {
        const helper = new CustomsRequestHelper(customsRequest);

        if (!helper.isSelfSubmissionTarget()) {
          return null;
        }

        return (
          <div className={classes.customsInstructionFileLinkSectionContainer}>
            <div className={classes.customsInstructionFileLinkSectionTitle}>
              {strings.acceptCustomsRequest}
            </div>

            {putCustomsRequestUtils.renderAlertsAndSpinnerModal()}

            {helper.getCurrentSubmissionStatus().some((s) => s.name === '通関準備中') ? (
              <div className={clsx(classes.textBlack)}>{strings.customsRequestAccepted}</div>
            ) : (
              <>
                <div className={clsx(classes.textGray500, classes.smallerText)}>
                  {strings.customRequestNotYetAccepted}
                </div>

                <div style={{ height: 8 }} />

                {pipe(helper.canAccept(user, bookingInquiry), (canAccept) => (
                  <Button
                    color={canAccept._tag === 'Right' ? 'primary' : 'grey'}
                    shape="round"
                    size="small"
                    text={strings.accept}
                    type="button"
                    onClick={async () => {
                      await foldMessagesEither(
                        canAccept,
                        async (e) => {
                          await confirm({
                            message: e.messages,
                          });
                        },
                        async () => {
                          if (
                            'confirmed' ===
                            (await confirm({
                              message: strings.warningComplete,
                            }))
                          ) {
                            await putCustomsRequest.mutateAsync({});
                          }
                        },
                      );
                    }}
                  />
                ))}
              </>
            )}
          </div>
        );
      },
    );

  const renderCustomsRequestFooter = () => {
    return mapQueriesData(
      {
        customsRequest: customsRequestQuery,
      },
      ({ customsRequest }) => {
        const lastCreatedOrSent = customsRequest.status.findLast(
          (s) => s.name === '通関依頼書作成済' || s.name === '通関依頼書送信済',
        );

        if (!lastCreatedOrSent) return null;

        return (
          <div className={clsx(classes.textGray500, classes.smallerText)} style={{ fontSize: 11 }}>
            {strings.customsRequestFooterStatusMap[lastCreatedOrSent.name]}{' '}
            {`(${toJST(new Date(lastCreatedOrSent.time))})`}
          </div>
        );
      },
    );
  };

  return (
    <div className={classes.customsInstructionFilesContainer}>
      {renderDialog()}

      <div className={classes.customsInstructionFilesTitle}>
        <span className={clsx(classes.textBlack, classes.normalText, classes.fontWeight600)}>
          {instructionStrings.customsRelatedFiles}
        </span>
        <a className={clsx(classes.link, classes.smallerText)} onClick={onClickDownloadAll}>
          {strings.downloadAll}
        </a>
      </div>

      {downloadAndCompressUtils.renderAlertsAndSpinnerModal()}

      <div className={classes.customsInstructionFileLinkSectionListContainer}>
        {renderSalespersonCustoms()}

        {renderCompleteRequest()}

        {(customsRequestQuery.isSuccess ||
          isAxiosErrorOfStatus(customsRequestQuery.error, 404)) && (
          <FileLinkSection
            title={customsRequestStrings.customsRequest}
            files={files.customsRequest}
            noBottomBorder
            footer={renderCustomsRequestFooter()}
          />
        )}

        <SeaBLFile />

        <FileLinkSection title={arrivalNoticeStrings.arrivalNotice} files={files.arrivalNotice} />

        <FileLinkSection title={instructionStrings.customsFiles} files={files.customFiles} />

        {customsInstruction.insurance?.tag === 'BLANKET' && (
          <FileLinkSection
            title={instructionStrings.blanketInsuranceFiles}
            files={files.blanketInsuranceFiles}
          />
        )}

        <FileLinkSection title={instructionStrings.commodities} files={files.commodities} />
      </div>
    </div>
  );
};
